import { WagoApiClient } from '@shared/service/wago-api.client';
import { ModelConversionService } from '@shared/convert/model-conversion.service';
import { StringUtils } from '@shared/util/string-utils';
import { GenerationResponseVO } from '../../../manager/generation-response.vo';
import { map } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { PdfExportJournalEntityVO, ReportVO } from '../../../model/vo/report.vo';
import { PropertyVO } from '../../article/model/vo/property.vo';
import { UserProfileVO } from '@user/model/vo/user.vo';
import { ProjectVO } from '../../../model/vo/project.vo';
import { DateFormatter } from '@shared/date-formatter';
import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CameraParametersVO } from '@3d-scene/camera-parameters.vo';

@Injectable({ providedIn: 'root' })
export class ReportDataService {

  constructor(private wagoApiClient: WagoApiClient, private modelConversionService: ModelConversionService) { }

  public checkForRunningReportGeneration(userId: number): Observable<PdfExportJournalEntityVO> {
    return this.wagoApiClient.get('report/check/' + userId.toString());
  }

  public getReportTemplates(projectType: string, localeKey: string): Observable<ReportVO[]> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('projectType', projectType ? projectType : 'null');
    httpParams = httpParams.append('localeKey', localeKey);
    const requestOptions = {
      params: httpParams,
    };
    return this.wagoApiClient
      .get('report/templates', requestOptions, {
        isErrorHandlingRequired: false,
        askToElevateUserFromGuestByLoggingIn: true,
      })
      .pipe(map((response) => this.modelConversionService.convertReportVOs(response)));
  }

  public generatePDFReport(
    projectVO: ProjectVO,
    userProfile: UserProfileVO,
    reportsVO: ReportVO[],
    reportPropertiesVO: PropertyVO[],
    localeKey: string,
    cameraParameters: CameraParametersVO,
  ) {
    const reportTemplatePdfDTO: any = {
      userProfile,
      reportsVO,
      reportPropertiesVO,
      localeKey,
      projectVO,
      cameraParameters,
      timeZone: DateFormatter.userTimeZoneGMT(),
    };
    return this.wagoApiClient
      .post('report/generate/pdf', reportTemplatePdfDTO)
      .pipe(map((response) => this.modelConversionService.convertGenerationResponseVO(response)));
  }

  public generateCSVReportForMultipleProjects(
    projectIds: number[],
    projectGroupIds: number[],
    userProfile: UserProfileVO,
    reportsVO: ReportVO[],
    reportPropertiesVO: PropertyVO[],
    localeKey: string
  ): Observable<GenerationResponseVO> {
    const reportTemplateCsvMultiDTO: any = {
      projectIds,
      projectGroupIds,
      userProfile,
      reportsVO,
      reportPropertiesVO,
      localeKey,
    };
    return this.wagoApiClient
      .post('report/generate/csv/multiple', reportTemplateCsvMultiDTO)
      .pipe(map((response) => this.modelConversionService.convertGenerationResponseVO(response)));
  }

  public generateCSVReportForProject(
    projectVO: ProjectVO,
    userProfile: UserProfileVO,
    reportsVO: ReportVO[],
    reportPropertiesVO: PropertyVO[],
    localeKey: string
  ): Observable<GenerationResponseVO> {
    const reportTemplateCsvSingleDTO: any = {
      projectVO,
      userProfile,
      reportsVO,
      reportPropertiesVO,
      localeKey,
    };
    reportTemplateCsvSingleDTO.projectVO.representations = [];
    return this.wagoApiClient
      .post('report/generate/csv/single', reportTemplateCsvSingleDTO)
      .pipe(map((response) => this.modelConversionService.convertGenerationResponseVO(response)));
  }

  public checkReport(reportId: string): Observable<GenerationResponseVO> {
    if (StringUtils.isEmpty(reportId)) {
      return throwError(() => new Error('Report ID is empty'));
    }
    return this.wagoApiClient
      .post(`report/${reportId}/check`, reportId, undefined, {
        isErrorHandlingRequired: false,
        overlayType: null,
      })
      .pipe(
        map((response) => this.modelConversionService.convertGenerationResponseVO(response))
      );
  }


  public createLtlJsonInfoFile(projectId: number, infoJson: string): Observable<string> {
    return this.wagoApiClient.post('report/generate/json', {projectId, infoJson});
  }
}
